import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Product, CreateProductRequest } from '@/types/api';

interface ProductFormProps {
  initial?: Product;
  submitting: boolean;
  onSubmit: (data: CreateProductRequest) => void;
  onCancel: () => void;
  submitLabel: string;
}

interface NumericField {
  key: keyof Pick<
    CreateProductRequest,
    | 'energyKcal100g'
    | 'proteins100g'
    | 'carbohydrates100g'
    | 'fat100g'
    | 'fiber100g'
  >;
  labelKey: string;
}

const NUMERIC_FIELDS: NumericField[] = [
  { key: 'energyKcal100g', labelKey: 'products.energyKcal' },
  { key: 'proteins100g', labelKey: 'products.proteins' },
  { key: 'carbohydrates100g', labelKey: 'products.carbohydrates' },
  { key: 'fat100g', labelKey: 'products.fat' },
  { key: 'fiber100g', labelKey: 'products.fiber' },
];

export function ProductForm({
  initial,
  submitting,
  onSubmit,
  onCancel,
  submitLabel,
}: ProductFormProps) {
  const { t } = useTranslation();
  const [name, setName] = useState(initial?.name ?? '');
  const [brand, setBrand] = useState(initial?.brand ?? '');
  const [barcode, setBarcode] = useState(initial?.barcode ?? '');
  const [servingSize, setServingSize] = useState(initial?.servingSize ?? '');
  const [isFavorite, setIsFavorite] = useState(initial?.isFavorite ?? false);
  const [numbers, setNumbers] = useState<Record<string, string>>({
    energyKcal100g: initial?.energyKcal100g?.toString() ?? '',
    proteins100g: initial?.proteins100g?.toString() ?? '',
    carbohydrates100g: initial?.carbohydrates100g?.toString() ?? '',
    fat100g: initial?.fat100g?.toString() ?? '',
    fiber100g: initial?.fiber100g?.toString() ?? '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    const parse = (value: string): number | undefined => {
      if (value.trim() === '') return undefined;
      const parsed = parseFloat(value);
      return Number.isNaN(parsed) ? undefined : parsed;
    };

    const data: CreateProductRequest = {
      name: name.trim(),
      brand: brand.trim() || undefined,
      barcode: barcode.trim() || undefined,
      servingSize: servingSize.trim() || undefined,
      isFavorite,
      energyKcal100g: parse(numbers.energyKcal100g),
      proteins100g: parse(numbers.proteins100g),
      carbohydrates100g: parse(numbers.carbohydrates100g),
      fat100g: parse(numbers.fat100g),
      fiber100g: parse(numbers.fiber100g),
    };

    onSubmit(data);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('products.name')}
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('products.brand')}
          </label>
          <input
            type="text"
            value={brand}
            onChange={(e) => setBrand(e.target.value)}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('products.barcode')}
          </label>
          <input
            type="text"
            value={barcode}
            onChange={(e) => setBarcode(e.target.value)}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('products.servingSize')}
          </label>
          <input
            type="text"
            value={servingSize}
            onChange={(e) => setServingSize(e.target.value)}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {NUMERIC_FIELDS.map((field) => (
          <div key={field.key}>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t(field.labelKey)}
            </label>
            <input
              type="number"
              step="0.1"
              min="0"
              value={numbers[field.key]}
              onChange={(e) =>
                setNumbers((prev) => ({
                  ...prev,
                  [field.key]: e.target.value,
                }))
              }
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
            />
          </div>
        ))}
      </div>

      <label className="inline-flex items-center gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={isFavorite}
          onChange={(e) => setIsFavorite(e.target.checked)}
          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
        />
        {t('products.favorite')}
      </label>

      <div className="flex gap-3">
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
        >
          {submitting ? t('app.saving') : submitLabel}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          {t('app.cancel')}
        </button>
      </div>
    </form>
  );
}
